import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Chip,
    LinearProgress,
    Avatar,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import CellTowerIcon from '@mui/icons-material/CellTower';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import { createTbWebSocket } from '../../Services/app/tbWebSocketService';
import { telemetrykeydata, Deviceattributeget } from '../../Services/app/operatorservice';
import { useMachineGroups } from '../../Shared/hooks/useMachineGroups';
import PlantFilterSelect from '../../Shared/components/PlantFilterSelect';

const NOT_CONFIGURED_TIMEOUT_MS = 8000;
const TELEMETRY_KEY = 'simcard';
// "heartbeat" is a device ATTRIBUTE, not a timeseries key — it never arrives over the
// LATEST_TELEMETRY WS subscription used for simcard, so it's polled via REST instead.
const HEARTBEAT_KEY = 'heartbeat';
const HEARTBEAT_POLL_INTERVAL_MS = 30 * 1000;
const HISTORY_WINDOW_MS = 24 * 60 * 60 * 1000;
// Liveness is driven by the separate "heartbeat" attribute, not the simcard payload's
// own age — if no heartbeat has arrived in 10 minutes, treat the device as disconnected.
const HEARTBEAT_STALE_THRESHOLD_MS = 10 * 60 * 1000;
// Show a marker + value label at every point only while the series is sparse;
// past this it turns into unreadable clutter, so fall back to hover-only markers.
const MAX_POINTS_FOR_LABELS = 24;

const COLORS = {
    ink: '#101828',
    text: '#344054',
    sub: '#8A94A6',
    subtle: '#98A2B3',
    border: '#ECEFF3',
    divider: '#F0F1F3',
    page: '#F7F8FA',
    good: '#2E9E5B',
    warn: '#DD8A00',
    bad: '#E5484D',
    info: '#4C6FFF',
    violet: '#8A5CF6',
};

const trendOf = (series) => {
    if (series.length < 2) return { delta: 0, pct: 0 };
    const last = series[series.length - 1].y;
    const prev = series[series.length - 2].y;
    const delta = last - prev;
    const pct = prev !== 0 ? (delta / prev) * 100 : 0;
    return { delta, pct };
};

function TrendChip({ series, color }) {
    if (series.length === 0) return null;
    const last = series[series.length - 1].y;
    const { delta } = trendOf(series);
    const TrendIcon = delta > 0.05 ? TrendingUpIcon : delta < -0.05 ? TrendingDownIcon : TrendingFlatIcon;
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: COLORS.ink, lineHeight: 1 }}>
                {last.toFixed(2)} <Typography component="span" variant="caption" sx={{ color: COLORS.sub, fontWeight: 600 }}>Mbps</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', color }}>
                <TrendIcon sx={{ fontSize: 16 }} />
            </Box>
        </Box>
    );
}

const qualityColor = (quality) => {
    switch ((quality || '').toLowerCase()) {
        case 'excellent':
        case 'good':
            return '#2E9E5B';
        case 'fair':
        case 'average':
            return '#DD8A00';
        case 'poor':
        case 'bad':
            return '#E5484D';
        default:
            return '#8A94A6';
    }
};

function SectionCard({ icon, iconColor, title, action, children }) {
    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                borderTop: `3px solid ${iconColor}`,
                boxShadow: '0 1px 3px rgba(16,24,40,0.04)',
                transition: 'box-shadow .15s ease, transform .15s ease',
                '&:hover': { boxShadow: '0 8px 20px rgba(16,24,40,0.09)', transform: 'translateY(-1px)' },
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: `${iconColor}1A`, color: iconColor }}>
                            {icon}
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.text, letterSpacing: 0.2 }}>
                            {title}
                        </Typography>
                    </Box>
                    {action}
                </Box>
                {children}
            </CardContent>
        </Card>
    );
}

function StatRow({ label, value, mono }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: `1px dashed ${COLORS.divider}`, '&:last-of-type': { border: 0 } }}>
            <Typography component="span" variant="body2" sx={{ color: COLORS.sub }}>{label}</Typography>
            <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#1D2939', fontFamily: mono ? 'monospace' : 'inherit' }}>
                {value ?? '-'}
            </Typography>
        </Box>
    );
}

export default function HardwareStatus() {
    const customerId = localStorage.getItem('CustomerID');
    const {
        availableMachines, deviceNameID, selectedMachines, handleMachineChange, loading,
        plantOptions, selectedPlants, showPlantFilter, handlePlantChange,
    } = useMachineGroups(customerId, true);

    const [simcard, setSimcard] = useState(null);
    const [notConfigured, setNotConfigured] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [lastReceivedTs, setLastReceivedTs] = useState(null);
    const [speedHistory, setSpeedHistory] = useState([]); // [{ x: ts, download, upload }]
    const [historyLoading, setHistoryLoading] = useState(false);
    const [now, setNow] = useState(Date.now());
    const [lastHeartbeatTs, setLastHeartbeatTs] = useState(null);
    const [heartbeatNotConfigured, setHeartbeatNotConfigured] = useState(false);

    const wsRef = useRef(null);
    const notConfiguredTimerRef = useRef(null);
    const simcardRef = useRef(null);

    // Re-check staleness periodically even if no new telemetry ever arrives.
    useEffect(() => {
        const clock = setInterval(() => setNow(Date.now()), 60 * 1000);
        return () => clearInterval(clock);
    }, []);

    useEffect(() => {
        simcardRef.current = simcard;
    }, [simcard]);

    const deviceId = deviceNameID.find((d) => d.name === selectedMachines)?.id || null;

    const appendSpeedPoint = (ts, download, upload) => {
        if (ts === undefined || ts === null || isNaN(Number(ts))) return;
        const dl = Number(download);
        const ul = Number(upload);
        if (isNaN(dl) && isNaN(ul)) return;
        setSpeedHistory((prev) => {
            const cutoff = Date.now() - HISTORY_WINDOW_MS;
            const withoutSameTs = prev.filter((p) => p.x !== ts);
            const next = [...withoutSameTs, { x: ts, download: isNaN(dl) ? null : dl, upload: isNaN(ul) ? null : ul }]
                .filter((p) => p.x >= cutoff)
                .sort((a, b) => a.x - b.x);
            return next;
        });
    };

    useEffect(() => {
        if (!deviceId) return;

        let cancelled = false;
        const fetchHistory = async () => {
            setHistoryLoading(true);
            try {
                const now = Date.now();
                const res = await telemetrykeydata(deviceId, 'DEVICE', TELEMETRY_KEY, now - HISTORY_WINDOW_MS, now);
                const entries = res?.[TELEMETRY_KEY] || [];
                const points = entries
                    .map((entry) => {
                        try {
                            const parsed = typeof entry.value === 'string' ? JSON.parse(entry.value) : entry.value;
                            const dl = Number(parsed?.download_mbps);
                            const ul = Number(parsed?.upload_mbps);
                            if (isNaN(dl) && isNaN(ul)) return null;
                            return { x: Number(entry.ts), download: isNaN(dl) ? null : dl, upload: isNaN(ul) ? null : ul };
                        } catch {
                            return null;
                        }
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.x - b.x);
                if (!cancelled) setSpeedHistory(points);
            } catch (err) {
                console.error('Failed to fetch simcard history:', err);
                if (!cancelled) setSpeedHistory([]);
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        };
        fetchHistory();

        return () => { cancelled = true; };
    }, [deviceId]);

    // "heartbeat" is an attribute, so it's polled via REST rather than pushed over the
    // WS telemetry subscription (attributes don't flow through LATEST_TELEMETRY ts subs).
    useEffect(() => {
        if (!deviceId) return;

        let cancelled = false;
        setLastHeartbeatTs(null);
        setHeartbeatNotConfigured(false);

        const fetchHeartbeat = async () => {
            try {
                const res = await Deviceattributeget(deviceId, HEARTBEAT_KEY);
                if (cancelled) return;
                if (Array.isArray(res) && res.length > 0) {
                    const attr = res[0];
                    let ts = Number(attr.lastUpdateTs);
                    if (isNaN(ts)) {
                        try {
                            const parsed = typeof attr.value === 'string' ? JSON.parse(attr.value) : attr.value;
                            ts = Number(parsed?.ts);
                        } catch {
                            ts = NaN;
                        }
                    }
                    if (!isNaN(ts)) {
                        setLastHeartbeatTs(ts);
                        setHeartbeatNotConfigured(false);
                    }
                } else {
                    setHeartbeatNotConfigured(true);
                }
            } catch (err) {
                console.error('Failed to fetch heartbeat attribute:', err);
            }
        };
        fetchHeartbeat();
        const poll = setInterval(fetchHeartbeat, HEARTBEAT_POLL_INTERVAL_MS);

        return () => { cancelled = true; clearInterval(poll); };
    }, [deviceId]);

    useEffect(() => {
        if (!deviceId) return;

        setSimcard(null);
        setNotConfigured(false);
        setWsConnected(false);
        setLastReceivedTs(null);
        setSpeedHistory([]);
        simcardRef.current = null;

        const token = localStorage.getItem('token1');
        const base = (window._env_.SERVER_URL || '').replace(/\/$/, '');
        const wsUrl = base.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://')
            + '/api/ws/plugins/telemetry?token=' + token;

        const ws = createTbWebSocket({
            url: wsUrl,
            onOpen: () => {
                setWsConnected(true);
                ws.subscribe([deviceId]);
                clearTimeout(notConfiguredTimerRef.current);
                notConfiguredTimerRef.current = setTimeout(() => {
                    if (simcardRef.current === null) setNotConfigured(true);
                }, NOT_CONFIGURED_TIMEOUT_MS);
            },
            onMessage: (msg) => {
                const raw = msg.data?.[TELEMETRY_KEY]?.[0];
                if (!raw) return;
                clearTimeout(notConfiguredTimerRef.current);
                try {
                    const parsed = typeof raw[1] === 'string' ? JSON.parse(raw[1]) : raw[1];
                    setSimcard(parsed);
                    setNotConfigured(false);
                    setLastReceivedTs(raw[0]);
                    appendSpeedPoint(Number(raw[0]), parsed?.download_mbps, parsed?.upload_mbps);
                } catch (err) {
                    console.error('Failed to parse simcard telemetry:', err);
                }
            },
            onClose: () => {
                setWsConnected(false);
                setTimeout(() => ws.connect(), 3000);
            },
            onError: () => setWsConnected(false),
        });
        ws.connect();
        wsRef.current = ws;

        return () => {
            clearTimeout(notConfiguredTimerRef.current);
            ws.disconnect();
            wsRef.current = null;
        };
    }, [deviceId]);

    const signal = simcard?.signal || {};
    const sigColor = qualityColor(signal.quality);

    const heartbeatStale = lastHeartbeatTs !== null && (now - Number(lastHeartbeatTs)) > HEARTBEAT_STALE_THRESHOLD_MS;
    // The device can only be "Connected" if it says so AND its heartbeat is fresh —
    // a stale heartbeat means we can no longer trust that last-reported state.
    const effectiveConnected = !!simcard?.connected && !heartbeatStale;

    const downloadSeries = speedHistory
        .filter((p) => p.download !== null)
        .map((p) => ({ x: p.x, y: p.download }));
    const uploadSeries = speedHistory
        .filter((p) => p.upload !== null)
        .map((p) => ({ x: p.x, y: p.upload }));

    const speedChartOptions = (color, series) => {
        const showLabels = series.length > 0 && series.length <= MAX_POINTS_FOR_LABELS;
        return {
            chart: { toolbar: { show: false }, zoom: { enabled: false } },
            colors: [color],
            stroke: { width: 2.5, curve: 'smooth' },
            fill: {
                type: 'gradient',
                gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] },
            },
            markers: {
                size: showLabels ? 4 : 0,
                colors: ['#fff'],
                strokeColors: color,
                strokeWidth: 2,
                hover: { size: 6 },
            },
            dataLabels: {
                enabled: showLabels,
                formatter: (v) => v.toFixed(1),
                offsetY: -10,
                style: { fontSize: '10px', fontWeight: 700, colors: [color] },
                background: { enabled: true, foreColor: '#fff', borderRadius: 4, padding: 3, opacity: 0.9, borderWidth: 0 },
            },
            grid: { borderColor: COLORS.divider, strokeDashArray: 4, padding: { top: showLabels ? 20 : 0 } },
            xaxis: {
                type: 'datetime',
                // ApexCharts renders datetime axes in UTC by default — force local time
                // (matches the "Updated" timestamp elsewhere on this screen) instead of UTC.
                labels: { datetimeUTC: false, style: { colors: COLORS.sub, fontSize: '11px' } },
            },
            yaxis: {
                labels: {
                    style: { colors: COLORS.sub, fontSize: '11px' },
                    formatter: (v) => `${v.toFixed(1)}`,
                },
                title: { text: 'Mbps', style: { color: COLORS.sub, fontSize: '11px' } },
            },
            tooltip: { x: { format: 'dd MMM, HH:mm' } },
        };
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, minHeight: '100%', background: COLORS.page }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.ink }}>Hardware Status</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.sub, mt: 0.25 }}>
                        Live SIM &amp; connectivity monitoring
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                        size="small"
                        icon={wsConnected ? <WifiIcon sx={{ fontSize: 16 }} /> : <WifiOffIcon sx={{ fontSize: 16 }} />}
                        label={wsConnected ? 'Live' : 'Reconnecting…'}
                        sx={{
                            fontWeight: 600,
                            bgcolor: wsConnected ? '#EAF7EF' : '#FDF0F0',
                            color: wsConnected ? COLORS.good : COLORS.bad,
                            '& .MuiChip-icon': { color: 'inherit' },
                        }}
                    />
                    {showPlantFilter && (
                        <PlantFilterSelect plantOptions={plantOptions} selectedPlants={selectedPlants} onChange={handlePlantChange} />
                    )}
                    <FormControl size="small" sx={{ minWidth: 220, bgcolor: '#fff', borderRadius: 1 }}>
                        <InputLabel>Device</InputLabel>
                        <Select
                            value={selectedMachines || ''}
                            label="Device"
                            onChange={(e) => handleMachineChange(e.target.value)}
                        >
                            {availableMachines.map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress />
                </Box>
            ) : !selectedMachines ? (
                <Box sx={{ textAlign: 'center', mt: 10 }}>
                    <Typography variant="h6" sx={{ color: COLORS.sub }}>No devices available.</Typography>
                </Box>
            ) : notConfigured ? (
                <Box sx={{ textAlign: 'center', mt: 10 }}>
                    <CellTowerIcon sx={{ fontSize: 40, color: '#D0D5DD', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#667085', fontWeight: 600 }}>
                        "{TELEMETRY_KEY}" key is not configured for this device
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.subtle, mt: 0.5 }}>
                        No telemetry has been received for this key yet.
                    </Typography>
                </Box>
            ) : !simcard ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 10 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" sx={{ color: COLORS.sub }}>Waiting for live data…</Typography>
                </Box>
            ) : (
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6} md={3}>
                        <SectionCard
                            icon={effectiveConnected ? <WifiIcon fontSize="small" /> : <WifiOffIcon fontSize="small" />}
                            iconColor={effectiveConnected ? COLORS.good : COLORS.bad}
                            title="Connection"
                            action={
                                <Chip
                                    size="small"
                                    label={effectiveConnected ? 'Connected' : 'Disconnected'}
                                    sx={{
                                        fontWeight: 700,
                                        bgcolor: effectiveConnected ? '#EAF7EF' : '#FDF0F0',
                                        color: effectiveConnected ? COLORS.good : COLORS.bad,
                                    }}
                                />
                            }
                        >
                            <StatRow label="IP Address" value={simcard.ppp0_ip} mono />
                            <StatRow
                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AccessTimeIcon sx={{ fontSize: 15 }} />Updated</Box>}
                                value={simcard.updated || (lastReceivedTs ? dayjs(lastReceivedTs).format('DD-MM-YYYY HH:mm:ss') : '-')}
                            />
                            <StatRow
                                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AccessTimeIcon sx={{ fontSize: 15 }} />Last Heartbeat</Box>}
                                value={lastHeartbeatTs ? dayjs(lastHeartbeatTs).format('DD-MM-YYYY HH:mm:ss') : '-'}
                            />
                            {heartbeatNotConfigured ? (
                                <Typography variant="caption" sx={{ display: 'block', color: COLORS.subtle, mt: 1 }}>
                                    "{HEARTBEAT_KEY}" key is not configured for this device.
                                </Typography>
                            ) : heartbeatStale ? (
                                <Chip
                                    size="small"
                                    icon={<WifiOffIcon sx={{ fontSize: 14 }} />}
                                    label="Heartbeat lost — no update for 10+ min"
                                    sx={{
                                        mt: 1,
                                        fontWeight: 600,
                                        bgcolor: '#FDF0F0',
                                        color: COLORS.bad,
                                        '& .MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                            ) : lastHeartbeatTs ? (
                                <Chip
                                    size="small"
                                    icon={<WifiIcon sx={{ fontSize: 14 }} />}
                                    label="Heartbeat OK"
                                    sx={{
                                        mt: 1,
                                        fontWeight: 600,
                                        bgcolor: '#EAF7EF',
                                        color: COLORS.good,
                                        '& .MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                            ) : null}
                        </SectionCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <SectionCard
                            icon={<SignalCellularAltIcon fontSize="small" />}
                            iconColor={sigColor}
                            title="Signal"
                            action={
                                <Chip
                                    size="small"
                                    label={signal.quality || 'Unknown'}
                                    sx={{ fontWeight: 700, bgcolor: `${sigColor}1A`, color: sigColor }}
                                />
                            }
                        >
                            <Box sx={{ mb: 1.5 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, Math.max(0, Number(signal.percent) || 0))}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#F0F1F3',
                                        '& .MuiLinearProgress-bar': { bgcolor: sigColor, borderRadius: 4 },
                                    }}
                                />
                            </Box>
                            <StatRow label="CSQ" value={signal.csq !== undefined ? `${signal.csq} (${signal.csq_range || '-'})` : '-'} />
                            <StatRow label="dBm" value={signal.dbm} />
                            <StatRow label="Percent" value={signal.percent !== undefined ? `${signal.percent}%` : '-'} />
                        </SectionCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <SectionCard icon={<SpeedIcon fontSize="small" />} iconColor={COLORS.info} title="Speed">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, textAlign: 'center' }}>
                                <Box sx={{ flex: 1 }}>
                                    <SpeedIcon sx={{ color: COLORS.sub, fontSize: 18 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1D2939' }}>
                                        {simcard.ping_latency_ms ?? '-'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: COLORS.sub }}>ms ping</Typography>
                                </Box>
                                <Box sx={{ flex: 1, borderLeft: `1px solid ${COLORS.divider}`, borderRight: `1px solid ${COLORS.divider}` }}>
                                    <ArrowDownwardIcon sx={{ color: COLORS.good, fontSize: 18 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1D2939' }}>
                                        {simcard.download_mbps ?? '-'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: COLORS.sub }}>Mbps download</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <ArrowUpwardIcon sx={{ color: COLORS.warn, fontSize: 18 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1D2939' }}>
                                        {simcard.upload_mbps ?? '-'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: COLORS.sub }}>Mbps upload</Typography>
                                </Box>
                            </Box>
                        </SectionCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <SectionCard icon={<CellTowerIcon fontSize="small" />} iconColor={COLORS.violet} title="Network">
                            <StatRow label="Operator" value={simcard.operator} />
                            <StatRow label="Network" value={simcard.network && <Chip size="small" label={simcard.network} sx={{ fontWeight: 700, bgcolor: '#F0EEFE', color: COLORS.violet }} />} />
                            <StatRow label="Band" value={simcard.band} mono />
                        </SectionCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SectionCard
                            icon={<ArrowDownwardIcon fontSize="small" />}
                            iconColor={COLORS.good}
                            title="Download Speed — Last 24 Hours"
                            action={<TrendChip series={downloadSeries} color={COLORS.good} />}
                        >
                            {historyLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : downloadSeries.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography variant="body2" sx={{ color: COLORS.subtle }}>No data available</Typography>
                                </Box>
                            ) : (
                                <ReactApexChart
                                    type="area"
                                    height={260}
                                    series={[{ name: 'Download (Mbps)', data: downloadSeries }]}
                                    options={speedChartOptions(COLORS.good, downloadSeries)}
                                />
                            )}
                        </SectionCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SectionCard
                            icon={<ArrowUpwardIcon fontSize="small" />}
                            iconColor={COLORS.warn}
                            title="Upload Speed — Last 24 Hours"
                            action={<TrendChip series={uploadSeries} color={COLORS.warn} />}
                        >
                            {historyLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : uploadSeries.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography variant="body2" sx={{ color: COLORS.subtle }}>No data available</Typography>
                                </Box>
                            ) : (
                                <ReactApexChart
                                    type="area"
                                    height={260}
                                    series={[{ name: 'Upload (Mbps)', data: uploadSeries }]}
                                    options={speedChartOptions(COLORS.warn, uploadSeries)}
                                />
                            )}
                        </SectionCard>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
